import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Sort,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Search,
  DragIndicator
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import GestionModulosForm from './GestionModulosForm';

function GestionModulos() {
  const server = process.env.REACT_APP_API_ROUTE;
  const { user } = useUser();
  const { tienePermiso } = useAccesos();

  // Estados principales
  const [modulos, setModulos] = useState([]);
  const [filteredModulos, setFilteredModulos] = useState([]);
  const [tiposModulo, setTiposModulo] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);

  // Estados para análisis por módulo
  const [analisisPorModulo, setAnalisisPorModulo] = useState([]);
  const [moduloSeleccionado, setModuloSeleccionado] = useState(null);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);
  const [pageAnalisis, setPageAnalisis] = useState(0);
  const [rowsPerPageAnalisis, setRowsPerPageAnalisis] = useState(5);

  // Estados para el formulario
  const [openForm, setOpenForm] = useState(false);
  const [editingModulo, setEditingModulo] = useState(null);

  // Estados para el modal de confirmación de eliminación
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [moduloToDelete, setModuloToDelete] = useState(null);

  // Estado para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Función para cargar módulos desde la API
  const fetchModulos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${server}/MgModuloes/porIdTipo/1`);
      
      if (response.ok) {
        const data = await response.json();
        const modulosData = data.$values || data || [];
        
        // Ordenar: primero los que tienen orden (por valor de orden), luego los que no tienen (por nombre)
        const sortedModulos = modulosData.sort((a, b) => {
          // Primero verificar si alguno no tiene orden (undefined o null)
          const aHasOrder = a.orden !== undefined && a.orden !== null;
          const bHasOrder = b.orden !== undefined && b.orden !== null;
          
          // Si uno tiene orden y el otro no, el que tiene orden va primero
          if (aHasOrder && !bHasOrder) return -1;
          if (!aHasOrder && bHasOrder) return 1;
          
          // Si ambos tienen orden, comparar por valor de orden
          if (aHasOrder && bHasOrder) {
            return a.orden - b.orden;
          }
          
          // Si ninguno tiene orden, comparar por nombre (ambos van al final)
          return (a.nombreModulo || '').localeCompare(b.nombreModulo || '');
        });
        
        setModulos(sortedModulos);
        setFilteredModulos(sortedModulos);
      } else {
        console.error('Error al cargar módulos:', response.statusText);
        alert('Error al cargar los módulos');
      }
    } catch (error) {
      console.error('Error al cargar módulos:', error);
      alert('Error al cargar los módulos');
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar tipos de módulo
  const fetchTiposModulo = async () => {
    try {
      const response = await fetch(`${server}/MgModuloes`);
      if (response.ok) {
        const data = await response.json();
        setTiposModulo(data.$values || data || []);
      }
    } catch (error) {
      console.error('Error al cargar tipos de módulo:', error);
    }
  };

  // Función para obtener el nombre del tipo de módulo
  const getTipoModuloNombre = (idTipo) => {
    const tipo = tiposModulo.find(t => t.idTipo === idTipo);
    return tipo ? tipo.descripcionTipo : idTipo || 'N/A';
  };

  // Función para cargar análisis por módulo
  const fetchAnalisisPorModulo = async (idModulo) => {
    if (!idModulo) return;
    
    setLoadingAnalisis(true);
    try {
      const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/porModulo/${idModulo}`);
      if (response.ok) {
        const data = await response.json();
        const analisisData = data.$values || data || [];
        
        // Ordenar por campo orden si existe, sino por descripción
        const sortedAnalisis = analisisData.sort((a, b) => {
          if (a.orden && b.orden) {
            return a.orden - b.orden;
          }
          return (a.descripcionTipoAnalisis || '').localeCompare(b.descripcionTipoAnalisis || '');
        });
        
        setAnalisisPorModulo(sortedAnalisis);
      } else {
        console.error('Error al cargar análisis del módulo:', response.statusText);
        setAnalisisPorModulo([]);
      }
    } catch (error) {
      console.error('Error al cargar análisis del módulo:', error);
      setAnalisisPorModulo([]);
    } finally {
      setLoadingAnalisis(false);
    }
  };

  // Función para actualizar orden de análisis
  const updateOrdenAnalisis = async (idTipoAnalisis, nuevoOrden) => {
    try {
      const usuario = user?.codigoUsuario || 'ADMIN';
      const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/ordenar/${idTipoAnalisis}/${usuario}/${nuevoOrden}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al actualizar orden');
      }
    } catch (error) {
      console.error('Error al actualizar orden:', error);
      throw error;
    }
  };

  // Función para mover análisis hacia arriba
  const handleMoveUpAnalisis = async (index) => {
    if (index === 0) return;
    
    const newList = [...analisisParaOrdenar];
    const analisisActual = newList[index];
    const analisisAnterior = newList[index - 1];
    
    // Intercambiar posiciones
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    
    try {
      // Solo actualizar los dos análisis que se intercambiaron
      await updateOrdenAnalisis(analisisActual.idTipoAnalisis, analisisAnterior.orden);
      await updateOrdenAnalisis(analisisAnterior.idTipoAnalisis, analisisActual.orden);
      
      // Actualizar estado local con los nuevos órdenes
      newList[index - 1].orden = analisisActual.orden;
      newList[index].orden = analisisAnterior.orden;
      
      setAnalisisParaOrdenar(newList);
    } catch (error) {
      alert('Error al actualizar el orden: ' + error.message);
    }
  };

  // Función para mover análisis hacia abajo
  const handleMoveDownAnalisis = async (index) => {
    if (index === analisisParaOrdenar.length - 1) return;
    
    const newList = [...analisisParaOrdenar];
    const analisisActual = newList[index];
    const analisisSiguiente = newList[index + 1];
    
    // Intercambiar posiciones
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    
    try {
      // Solo actualizar los dos análisis que se intercambiaron
      await updateOrdenAnalisis(analisisActual.idTipoAnalisis, analisisSiguiente.orden);
      await updateOrdenAnalisis(analisisSiguiente.idTipoAnalisis, analisisActual.orden);
      
      // Actualizar estado local con los nuevos órdenes
      newList[index].orden = analisisSiguiente.orden;
      newList[index + 1].orden = analisisActual.orden;
      
      setAnalisisParaOrdenar(newList);
    } catch (error) {
      alert('Error al actualizar el orden: ' + error.message);
    }
  };

  // Función para actualizar orden de módulos
  const updateOrdenModulo = async (idModulo, nuevoOrden) => {
    try {
      const response = await fetch(`${server}/MgModuloes/${idModulo}/orden`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          orden: nuevoOrden,
          modificadoPor: user?.codigoUsuario || 'ADMIN' 
        })
      });

      if (!response.ok) {
        throw new Error('Error al actualizar orden del módulo');
      }
    } catch (error) {
      console.error('Error al actualizar orden del módulo:', error);
      throw error;
    }
  };

  // Función para mover módulo hacia arriba
  const handleMoveUpModulo = async (index) => {
    if (index === 0) return;
    
    const newList = [...filteredModulos];
    const moduloActual = newList[index];
    const moduloAnterior = newList[index - 1];
    
    // Intercambiar posiciones
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    
    try {
      // Solo actualizar los dos módulos que se intercambiaron
      await updateOrdenModulo(moduloActual.idModulo, moduloAnterior.orden);
      await updateOrdenModulo(moduloAnterior.idModulo, moduloActual.orden);
      
      // Actualizar estado local con los nuevos órdenes
      newList[index - 1].orden = moduloActual.orden;
      newList[index].orden = moduloAnterior.orden;
      
      setFilteredModulos(newList);
      
      // También actualizar el array principal
      const updatedModulos = modulos.map(modulo => {
        const found = newList.find(nl => nl.idModulo === modulo.idModulo);
        return found || modulo;
      });
      setModulos(updatedModulos);
    } catch (error) {
      alert('Error al actualizar el orden: ' + error.message);
    }
  };

  // Función para mover módulo hacia abajo
  const handleMoveDownModulo = async (index) => {
    if (index === filteredModulos.length - 1) return;
    
    const newList = [...filteredModulos];
    const moduloActual = newList[index];
    const moduloSiguiente = newList[index + 1];
    
    // Intercambiar posiciones
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    
    try {
      // Solo actualizar los dos módulos que se intercambiaron
      await updateOrdenModulo(moduloActual.idModulo, moduloSiguiente.orden);
      await updateOrdenModulo(moduloSiguiente.idModulo, moduloActual.orden);
      
      // Actualizar estado local con los nuevos órdenes
      newList[index].orden = moduloSiguiente.orden;
      newList[index + 1].orden = moduloActual.orden;
      
      setFilteredModulos(newList);
      
      // También actualizar el array principal
      const updatedModulos = modulos.map(modulo => {
        const found = newList.find(nl => nl.idModulo === modulo.idModulo);
        return found || modulo;
      });
      setModulos(updatedModulos);
    } catch (error) {
      alert('Error al actualizar el orden: ' + error.message);
    }
  };

  // useEffect para cargar datos iniciales
  useEffect(() => {
    fetchModulos();
    fetchTiposModulo();
  }, []);

  // Filtrar módulos basado en la búsqueda
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredModulos(modulos);
    } else {
      const filtered = modulos.filter(modulo =>
        (modulo.nombreModulo || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (modulo.idModulo || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (modulo.etiquetaInforme || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredModulos(filtered);
    }
    setPage(0); // Resetear a la primera página cuando se filtra
  }, [searchQuery, modulos]);

  // Handlers para paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handlers para el formulario
  const handleOpenForm = () => {
    setEditingModulo(null);
    setOpenForm(true);
  };

  const handleEditModulo = (modulo) => {
    setEditingModulo(modulo);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingModulo(null);
  };

  const handleFormSuccess = () => {
    fetchModulos(); // Recargar la lista
    handleCloseForm();
  };

  // Handlers para eliminación
  const handleOpenDeleteDialog = (modulo) => {
    setModuloToDelete(modulo);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setModuloToDelete(null);
  };

  // Handlers para selección de módulo y análisis
  const handleSelectModulo = (modulo) => {
    setModuloSeleccionado(modulo);
    fetchAnalisisPorModulo(modulo.idModulo);
    setPageAnalisis(0); // Reset página de análisis
  };

  // Handlers para paginación de análisis
  const handleChangePageAnalisis = (event, newPage) => {
    setPageAnalisis(newPage);
  };

  const handleChangeRowsPerPageAnalisis = (event) => {
    setRowsPerPageAnalisis(parseInt(event.target.value, 10));
    setPageAnalisis(0);
  };

  // Función para actualizar múltiples órdenes de análisis
  const updateMultipleOrdenAnalisis = async (analisisToUpdate) => {
    const usuario = user?.codigoUsuario || 'ADMIN';
    const promises = [];
    
    console.log('Actualizando órdenes múltiples:', analisisToUpdate.map(a => ({
      id: a.idTipoAnalisis,
      descripcion: a.descripcionTipoAnalisis,
      ordenAnterior: a.ordenAnterior,
      ordenNuevo: a.orden
    })));

    for (const analisis of analisisToUpdate) {
      if (analisis.ordenAnterior !== analisis.orden) {
        const promise = fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/ordenar/${analisis.idTipoAnalisis}/${usuario}/${analisis.orden}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        promises.push(promise);
      }
    }

    const responses = await Promise.all(promises);
    
    // Verificar que todas las respuestas sean exitosas
    for (let i = 0; i < responses.length; i++) {
      if (!responses[i].ok) {
        const error = await responses[i].text();
        throw new Error(`Error al actualizar análisis: ${error}`);
      }
    }

    return responses;
  };

  // Función para manejar el drag and drop de análisis
  const handleDragEndAnalisis = async (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    // Ajustar índices por la paginación
    const pageOffset = pageAnalisis * rowsPerPageAnalisis;
    const globalSourceIndex = pageOffset + sourceIndex;
    const globalDestinationIndex = pageOffset + destinationIndex;

    console.log('Drag and drop - de índice global', globalSourceIndex, 'a índice global', globalDestinationIndex);

    // Crear una copia del array actual para evitar mutaciones
    const currentAnalisisList = [...analisisPorModulo];
    
    // Crear una nueva lista reordenada de TODOS los elementos
    const newAnalisisList = Array.from(currentAnalisisList);
    const [draggedItem] = newAnalisisList.splice(globalSourceIndex, 1);
    newAnalisisList.splice(globalDestinationIndex, 0, draggedItem);

    // Asignar nuevos órdenes secuenciales a todos los elementos
    const analisisToUpdate = newAnalisisList.map((analisis, index) => ({
      ...analisis,
      ordenAnterior: analisis.orden,
      orden: index + 1
    }));

    console.log('Nuevos órdenes a asignar:', analisisToUpdate.map(a => ({
      descripcion: a.descripcionTipoAnalisis,
      ordenAnterior: a.ordenAnterior,
      ordenNuevo: a.orden
    })));

    // Actualizar el estado local inmediatamente para mejor UX
    const updatedList = newAnalisisList.map((analisis, index) => ({
      ...analisis,
      orden: index + 1
    }));
    setAnalisisPorModulo(updatedList);

    try {
      await updateMultipleOrdenAnalisis(analisisToUpdate);
      
      console.log('Todos los órdenes de análisis actualizados correctamente');
      setSnackbar({
        open: true,
        message: 'Orden actualizado correctamente',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error al actualizar órdenes:', error);
      
      // Revertir el estado en caso de error
      setAnalisisPorModulo(currentAnalisisList);
      
      setSnackbar({
        open: true,
        message: error.message || 'Error al cambiar el orden de los análisis',
        severity: 'error'
      });
    }
  };

  // Función para mover análisis hacia arriba directamente
  const handleMoveUpAnalisisDirecto = async (globalIndex) => {
    if (globalIndex === 0) return;

    const itemActual = analisisPorModulo[globalIndex];
    const itemAnterior = analisisPorModulo[globalIndex - 1];

    console.log('Intercambiando órdenes de análisis:', {
      itemActual: {
        id: itemActual.idTipoAnalisis,
        descripcion: itemActual.descripcionTipoAnalisis,
        ordenActual: itemActual.orden,
        ordenNuevo: itemAnterior.orden
      },
      itemAnterior: {
        id: itemAnterior.idTipoAnalisis,
        descripcion: itemAnterior.descripcionTipoAnalisis,
        ordenActual: itemAnterior.orden,
        ordenNuevo: itemActual.orden
      }
    });

    try {
      const usuario = user?.codigoUsuario || 'ADMIN';
      
      // Intercambiar los valores de orden
      const response1 = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/ordenar/${itemActual.idTipoAnalisis}/${usuario}/${itemAnterior.orden}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response1.ok) {
        const error1 = await response1.text();
        throw new Error(`Error al actualizar orden del análisis ${itemActual.descripcionTipoAnalisis}: ${error1}`);
      }

      const response2 = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/ordenar/${itemAnterior.idTipoAnalisis}/${usuario}/${itemActual.orden}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response2.ok) {
        const error2 = await response2.text();
        throw new Error(`Error al actualizar orden del análisis ${itemAnterior.descripcionTipoAnalisis}: ${error2}`);
      }

      // Solo si ambas respuestas son OK, actualizar el estado local y mostrar mensaje de éxito
      const nuevaLista = [...analisisPorModulo];
      nuevaLista[globalIndex] = { ...itemActual, orden: itemAnterior.orden };
      nuevaLista[globalIndex - 1] = { ...itemAnterior, orden: itemActual.orden };
      
      // Reordenar por campo orden
      nuevaLista.sort((a, b) => (a.orden || 0) - (b.orden || 0));
      setAnalisisPorModulo(nuevaLista);

      console.log('Órdenes de análisis actualizados correctamente');
      setSnackbar({
        open: true,
        message: 'Orden actualizado correctamente',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error al mover análisis:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error al cambiar el orden del análisis',
        severity: 'error'
      });
    }
  };

  // Función para mover análisis hacia abajo directamente
  const handleMoveDownAnalisisDirecto = async (globalIndex) => {
    if (globalIndex === analisisPorModulo.length - 1) return;

    const itemActual = analisisPorModulo[globalIndex];
    const itemSiguiente = analisisPorModulo[globalIndex + 1];

    console.log('Intercambiando órdenes de análisis:', {
      itemActual: {
        id: itemActual.idTipoAnalisis,
        descripcion: itemActual.descripcionTipoAnalisis,
        ordenActual: itemActual.orden,
        ordenNuevo: itemSiguiente.orden
      },
      itemSiguiente: {
        id: itemSiguiente.idTipoAnalisis,
        descripcion: itemSiguiente.descripcionTipoAnalisis,
        ordenActual: itemSiguiente.orden,
        ordenNuevo: itemActual.orden
      }
    });

    try {
      const usuario = user?.codigoUsuario || 'ADMIN';
      
      // Intercambiar los valores de orden
      const response1 = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/ordenar/${itemActual.idTipoAnalisis}/${usuario}/${itemSiguiente.orden}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response1.ok) {
        const error1 = await response1.text();
        throw new Error(`Error al actualizar orden del análisis ${itemActual.descripcionTipoAnalisis}: ${error1}`);
      }

      const response2 = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/ordenar/${itemSiguiente.idTipoAnalisis}/${usuario}/${itemActual.orden}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response2.ok) {
        const error2 = await response2.text();
        throw new Error(`Error al actualizar orden del análisis ${itemSiguiente.descripcionTipoAnalisis}: ${error2}`);
      }

      // Solo si ambas respuestas son OK, actualizar el estado local y mostrar mensaje de éxito
      const nuevaLista = [...analisisPorModulo];
      nuevaLista[globalIndex] = { ...itemActual, orden: itemSiguiente.orden };
      nuevaLista[globalIndex + 1] = { ...itemSiguiente, orden: itemActual.orden };
      
      // Reordenar por campo orden
      nuevaLista.sort((a, b) => (a.orden || 0) - (b.orden || 0));
      setAnalisisPorModulo(nuevaLista);

      console.log('Órdenes de análisis actualizados correctamente');
      setSnackbar({
        open: true,
        message: 'Orden actualizado correctamente',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error al mover análisis:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error al cambiar el orden del análisis',
        severity: 'error'
      });
    }
  };

  const handleDeleteModulo = async () => {
    if (!moduloToDelete) return;

    try {
      const username = user?.codigoUsuario || 'ADMIN';
      const response = await fetch(`${server}/MgModuloes/${moduloToDelete.idModulo}/${username}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Módulo eliminado correctamente');
        fetchModulos(); // Recargar la lista
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || 
          (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : 
          'Error al eliminar el módulo');
        alert(`Error al eliminar el módulo: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error al eliminar módulo:', error);
      alert('Error al eliminar el módulo: ' + error.message);
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // Datos para mostrar en la tabla actual
  const paginatedModulos = filteredModulos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="right_col" role="main">
      <div className="page-title">
        <div className="title_left">
          <h3>Gestión de Módulos</h3>
        </div>
      </div>
      
      <div className="x_content">
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar módulos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleOpenForm}
                disabled={!tienePermiso('Crear Módulo')}
              >
                Nuevo Módulo
              </Button>
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>ID Módulo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nombre Módulo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Etiqueta Informe</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Orden</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ordenar</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography>Cargando...</Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedModulos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography>No se encontraron módulos</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedModulos.map((modulo, index) => (
                  <TableRow 
                    key={modulo.idModulo || index}
                    hover
                    onClick={() => handleSelectModulo(modulo)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: moduloSeleccionado?.idModulo === modulo.idModulo ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                      '&:hover': {
                        backgroundColor: moduloSeleccionado?.idModulo === modulo.idModulo ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {modulo.idModulo}
                      </Typography>
                    </TableCell>
                    <TableCell>{modulo.nombreModulo}</TableCell>
                    <TableCell>{getTipoModuloNombre(modulo.idTipo)}</TableCell>
                    <TableCell>{modulo.etiquetaInforme || '-'}</TableCell>
                    <TableCell>{modulo.orden || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="Mover arriba">
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveUpModulo(page * rowsPerPage + index);
                          }}
                          disabled={page * rowsPerPage + index === 0 || !tienePermiso('Ordenar Módulo')}
                          size="small"
                        >
                          <KeyboardArrowUp />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mover abajo">
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveDownModulo(page * rowsPerPage + index);
                          }}
                          disabled={page * rowsPerPage + index === filteredModulos.length - 1 || !tienePermiso('Ordenar Módulo')}
                          size="small"
                        >
                          <KeyboardArrowDown />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditModulo(modulo);
                          }}
                          disabled={!tienePermiso('Editar Módulo')}
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteDialog(modulo);
                          }}
                          disabled={!tienePermiso('Eliminar Módulo')}
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredModulos.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />

        {/* Sección de Análisis por Módulo */}
        {moduloSeleccionado && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2">
                  Análisis del Módulo: {moduloSeleccionado.nombreModulo}
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setPageAnalisis(0);
                      setRowsPerPageAnalisis(analisisPorModulo.length || 100);
                    }}
                    disabled={analisisPorModulo.length <= 5}
                  >
                    Ver Todos (Drag & Drop)
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <DragDropContext onDragEnd={handleDragEndAnalisis}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', width: '40px' }}>Arrastrar</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Clase</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Abreviatura</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Orden</TableCell>
                        {/* <TableCell sx={{ fontWeight: 'bold' }}>Ordenar</TableCell> */}
                      </TableRow>
                    </TableHead>
                    <Droppable droppableId="analisis-table">
                      {(provided, snapshot) => (
                        <TableBody
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          sx={{
                            backgroundColor: snapshot.isDraggingOver ? 'rgba(25, 118, 210, 0.04)' : 'inherit'
                          }}
                        >
                          {loadingAnalisis ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                <Typography>Cargando análisis...</Typography>
                              </TableCell>
                            </TableRow>
                          ) : analisisPorModulo.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center">
                                <Typography color="textSecondary">
                                  No hay análisis registrados para este módulo
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            analisisPorModulo
                              .slice(pageAnalisis * rowsPerPageAnalisis, pageAnalisis * rowsPerPageAnalisis + rowsPerPageAnalisis)
                              .map((analisis, index) => {
                                const globalIndex = pageAnalisis * rowsPerPageAnalisis + index;
                                const dragId = `drag-${analisis.idTipoAnalisis}-${globalIndex}`;
                                return (
                                  <Draggable 
                                    key={dragId} 
                                    draggableId={dragId} 
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <TableRow 
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        hover
                                        sx={{
                                          backgroundColor: snapshot.isDragging ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                          '&:hover': {
                                            backgroundColor: snapshot.isDragging ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                                          }
                                        }}
                                      >
                                        <TableCell>
                                          <div {...provided.dragHandleProps} style={{ pointerEvents: tienePermiso('Ordenar Análisis Módulo') ? 'auto' : 'none', opacity: tienePermiso('Ordenar Análisis Módulo') ? 1 : 0.3 }}>
                                            <DragIndicator 
                                              sx={{ 
                                                cursor: tienePermiso('Ordenar Análisis Módulo') ? 'grab' : 'not-allowed',
                                                color: 'text.secondary',
                                                '&:hover': { color: tienePermiso('Ordenar Análisis Módulo') ? 'primary.main' : 'text.secondary' }
                                              }} 
                                            />
                                          </div>
                                        </TableCell>
                                        <TableCell>{analisis.codigoAnalisis || '-'}</TableCell>
                                        <TableCell>{analisis.descripcionTipoAnalisis}</TableCell>
                                        <TableCell>{analisis.idClaseAnalisis}</TableCell>
                                        <TableCell>{analisis.abreviatura || '-'}</TableCell>
                                        <TableCell>
                                          <Chip 
                                            label={analisis.orden || '-'} 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                          />
                                        </TableCell>
                                        {/* <TableCell>
                                          <Tooltip title="Mover arriba">
                                            <IconButton 
                                              onClick={() => handleMoveUpAnalisisDirecto(globalIndex)} 
                                              disabled={globalIndex === 0}
                                              size="small"
                                            >
                                              <KeyboardArrowUp />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Mover abajo">
                                            <IconButton 
                                              onClick={() => handleMoveDownAnalisisDirecto(globalIndex)} 
                                              disabled={globalIndex === analisisPorModulo.length - 1}
                                              size="small"
                                            >
                                              <KeyboardArrowDown />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell> */}
                                      </TableRow>
                                    )}
                                  </Draggable>
                                );
                              })
                          )}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </Table>
                </TableContainer>
              </DragDropContext>

              <TablePagination
                component="div"
                count={analisisPorModulo.length}
                page={pageAnalisis}
                onPageChange={handleChangePageAnalisis}
                rowsPerPage={rowsPerPageAnalisis}
                onRowsPerPageChange={handleChangeRowsPerPageAnalisis}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Análisis por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
              />
            </CardContent>
          </Card>
        )}

        {/* Formulario Modal */}
        <GestionModulosForm
          open={openForm}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
          modulo={editingModulo}
          user={user}
        />

        {/* Modal de confirmación de eliminación */}
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirmar Eliminación</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Está seguro que desea eliminar el módulo "{moduloToDelete?.nombreModulo}"?
              Esta acción no se puede deshacer.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">
              Cancelar
            </Button>
            <Button onClick={handleDeleteModulo} color="error" autoFocus>
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

export default GestionModulos;